<script setup lang="ts">
import {
  computed,
  createLogger,
  onMounted,
  ref,
  shallowReactive,
  useRuntimeConfig,
  useSpots,
  useTemplateRef,
  watch,
} from "#imports";
import { until } from "@vueuse/core";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// this component should not be used more than once per page
// this component only runs in browsers, never on ssr
// prop.spotIds can change, but is not meant to render completely different
// lists of spots, rather a subset of a list, for filtering

const props = withDefaults(
  defineProps<{
    spotIds: string[];
    gestureHandling?: "cooperative" | "greedy";
    padding?: { top?: number; right?: number; bottom?: number; left?: number };
  }>(),
  {
    gestureHandling: "greedy",
    padding: () => ({ top: 0, right: 0, bottom: 0, left: 0 }),
  },
);

const emits = defineEmits<{
  spotClick: [value: string];
  zoomed: [value: number];
  dragged: [];
}>();

const spotStore = useSpots();
const logger = createLogger("MPMap");

const isLoading = ref<boolean>(true);
const spots = computed(() => spotStore.getByIds(props.spotIds).value);

const mapElement = useTemplateRef("mapElement");

let map: mapboxgl.Map; // instance
let markerTemplate: HTMLElement;

const fitSpotBounds = () => {
  if (!map || spots.value.length === 0) {
    // map or spots not loaded
    return;
  }
  let minLat: number | undefined = undefined;
  let maxLat: number | undefined = undefined;
  let minLng: number | undefined = undefined;
  let maxLng: number | undefined = undefined;

  for (const spot of spots.value) {
    if (typeof minLat === "undefined" || spot.lat < minLat) minLat = spot.lat;
    if (typeof maxLat === "undefined" || spot.lat > maxLat) maxLat = spot.lat;

    if (typeof minLng === "undefined" || spot.lon < minLng) minLng = spot.lon;
    if (typeof maxLng === "undefined" || spot.lon > maxLng) maxLng = spot.lon;
  }
  map.fitBounds(
    [
      [minLng!, minLat!],
      [maxLng!, maxLat!],
    ],
    { padding: props.padding, animate: false },
  );
};

// drawnSpots[spot.id] = mapMarker;
const drawnSpots = shallowReactive<
  Record<string, InstanceType<typeof mapboxgl.Marker>>
>({});

const drawSpots = () => {
  const stopTimer = logger.time("drawSpots");
  for (const spotId in drawnSpots) {
    if (!spots.value.find((s) => s.id === spotId)) {
      // spot removed
      drawnSpots[spotId]!.remove();
    }
  }

  for (const spot of spots.value) {
    if (drawnSpots[spot.id]) {
      // already drawn
      drawnSpots[spot.id]!.addTo(map);
    } else {
      // new spot
      const spotMarker = markerTemplate.cloneNode(
        true,
      ) as typeof markerTemplate;
      spotMarker.title = spot.name ?? "Unnamed location";

      const marker = new mapboxgl.Marker({
        element: spotMarker,
        anchor: "center",
      })
        .setLngLat([spot.lon, spot.lat])
        .addTo(map!);

      marker
        .getElement()
        .addEventListener("click", () => emits("spotClick", spot.id));

      drawnSpots[spot.id] = marker;
    }
  }
  stopTimer(`spots: ${spots.value.length}`);
};

watch(
  () => props.spotIds,
  async (newValue, oldValue) => {
    if (newValue.join(",") === oldValue?.join(",")) {
      // nothing changed
      return;
    }
    isLoading.value = true;
    await spotStore.fetchByIds(props.spotIds);
    isLoading.value = false;
    if (map) {
      // map already drawn
      if (
        selectedSpotId.value !== null &&
        !props.spotIds.includes(selectedSpotId.value)
      ) {
        selectedSpotId.value = null;
      }
      fitSpotBounds();
      drawSpots();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  const config = useRuntimeConfig();

  markerTemplate = document.createElement("div");
  markerTemplate.style.color = "var(--color-rose-50)";
  markerTemplate.style.backgroundColor = "var(--color-rose-400)";
  markerTemplate.style.borderRadius = "100%";
  markerTemplate.style.border = "1px solid var(--color-rose-500)";
  markerTemplate.style.width = "14px";
  markerTemplate.style.height = "14px";
  markerTemplate.style.boxShadow = "var(--shadow-sm)";
  markerTemplate.style.cursor = "pointer";

  // wait for data to load and mapElement to be present before drawing map
  await until(() => !isLoading.value && !!mapElement.value).toBeTruthy();

  map = new mapboxgl.Map({
    accessToken: config.public.mapboxKey as string,
    container: mapElement.value!,
    style: "mapbox://styles/mapbox/standard",
    maxZoom: 15,
  });

  map.on("zoom", () => emits("zoomed", map.getZoom()!));
  map.on("moveend", () => emits("dragged"));

  if (selectedSpot.value) {
    map.flyTo({
      center: {
        lat: selectedSpot.value.lat,
        lng: selectedSpot.value.lon,
      },
      zoom: 15,
      animate: false,
    });
  } else {
    fitSpotBounds();
  }
  drawSpots();
});

const selectedSpotId = ref<string | null>(null);
const selectedSpot = computed(() =>
  spots.value.find((s) => s.id === selectedSpotId.value),
);
const selectSpot = async (spotId: string | null) => {
  selectedSpotId.value = spotId;
  if (spotId === null || !selectedSpot.value) {
    return;
  }
  if (map) {
    map.flyTo({
      center: { lat: selectedSpot.value.lat, lng: selectedSpot.value.lon },
      zoom: 15,
    });
  }
};

defineExpose({
  selectSpot,
  fitSpotBounds,
});
</script>

<template>
  <div ref="mapElement" class="map-container"></div>
</template>

<style lang="postcss" scoped>
.map-container {
  grid-column: 1/-1;
  width: 100%;
  height: 100%;
  display: block;
  flex-grow: 1;
}
</style>
